/**
 * src/components/mfa/SecuritySettings.tsx
 * 「ログイン・セキュリティ」（/account/security）の中身。追加認証の初期設定が必要なときは /account でも使う。
 *   - 設定済みの認証方式の表示
 *   - パスキー管理（登録 / 一覧 / 名称変更 / 削除 / 複数登録 / 最終利用日時）
 *   - アシスタント: 認証方式の選択・変更（パスキー or メール）、メールアドレスの確認
 *   - 管理者: パスキー必須の案内
 * 再認証が必要な操作は useReauth がサーバーの指示に従って本人確認を挟む。
 */

import { useCallback, useEffect, useState } from 'react'
import { MfaApiError, METHOD_LABEL, mfaFetch, type MfaMethod, type MfaSettings } from '../../api/mfa'
import {
  createPasskey, describePasskeyError, isPasskeySupported, isPlatformAuthenticatorAvailable, type PasskeyTarget,
} from '../../lib/webauthn'
import { useAuth } from '../AuthGuard'
import { useReauth } from './useReauth'
import { EmailConfirmDialog, EmailVerifyPanel, useEmailVerify } from './EmailVerify'
import AssistantSecurity from './AssistantSecurity'
import { needsEmailGate } from './setupFlow'

function fmtDate(v: string | null | undefined): string {
  if (!v) return '—'
  const iso = v.includes('T') ? v : v.replace(' ', 'T') + 'Z'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return v
  return d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

type Msg = { ok: boolean; text: string } | null

function MsgBanner({ msg }: { msg: Msg }) {
  if (!msg) return null
  return (
    <div role={msg.ok ? 'status' : 'alert'} style={{
      background: msg.ok ? 'var(--green-light)' : 'var(--red-light)',
      color: msg.ok ? 'var(--green)' : 'var(--red)',
      borderRadius: 8, padding: '10px 14px', fontSize: 13, margin: '12px 0', lineHeight: 1.6,
    }}>
      {msg.ok ? '✓ ' : '⚠ '}{msg.text}
    </div>
  )
}

const sectionPad = (isMobile: boolean) => ({ padding: isMobile ? '16px 16px' : '20px 24px' })

export default function SecuritySettings({ isMobile, forceSetup }: { isMobile: boolean; forceSetup: boolean }) {
  const { refreshUser } = useAuth()
  const { withReauth, dialog } = useReauth()

  const [s, setS] = useState<MfaSettings | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [msg, setMsg] = useState<Msg>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [passkeyName, setPasskeyName] = useState('')
  const [platformOk, setPlatformOk] = useState<boolean | null>(null)

  const load = useCallback(async () => {
    try {
      setS(await mfaFetch<MfaSettings>('/auth/mfa/settings'))
      setLoadErr(null)
    } catch (e) {
      setLoadErr(e instanceof MfaApiError ? e.message : '設定を読み込めませんでした。')
    }
  }, [])

  useEffect(() => { void load() }, [load])
  useEffect(() => { void isPlatformAuthenticatorAvailable().then(setPlatformOk) }, [])

  const afterChange = async (okText: string) => {
    setMsg({ ok: true, text: okText })
    await load()
    await refreshUser()
  }

  // メールアドレスの確認（ステップ式の STEP1・ダイアログ・「メールアドレスの登録・確認」で共通の状態）
  const ev = useEmailVerify({
    withReauth,
    currentEmail: s?.email.address ?? '',
    onDone: async () => { await load(); await refreshUser() },
  })

  // メールが未確認のまま「パスキーを登録／メール認証を設定」しようとしたときの確認ダイアログ（アシスタントのみ）。
  // 確認が済んだら選んでいた操作を自動で続け、キャンセルなら何も変えずに元の画面へ戻る
  const [gate, setGate] = useState<null | { label: string; run: () => void }>(null)
  const emailOk = !!s && s.email.verified && !s.email.placeholder
  useEffect(() => {
    if (!gate || !emailOk) return
    const g = gate
    setGate(null)
    g.run()
  }, [gate, emailOk])

  // 設定完了（setup → full に昇格）したらホームへ
  useEffect(() => {
    if (!(forceSetup && s && !s.setup_required && s.method !== 'none')) return undefined
    const t = setTimeout(() => window.location.replace('/'), 1500)
    return () => clearTimeout(t)
  }, [forceSetup, s])

  if (loadErr) return <MsgBanner msg={{ ok: false, text: loadErr }} />
  if (!s) return <div style={{ fontSize: 13, color: 'var(--ink-3)', padding: 12 }}>読み込み中...</div>

  const isAdminUser = s.role === 'admin'
  const supported = isPasskeySupported()
  const hasPasskey = s.passkeys.length > 0
  const usablePasskeys = s.passkeys.filter((p) => p.usable).length
  const emailReady = s.email.verified && !s.email.placeholder
  // パスキーがまだ使えない（サーバー側で未導入）かつ登録済みのパスキーも無い → 「準備中」と表示し、登録欄は出さない
  const passkeyPreparing = !s.passkey_available && !hasPasskey

  // ── 操作 ─────────────────────────────────────────────────
  /** target: 保存先の希望（'phone' = 自分のスマホへ QR 経由／'device' = いま使っている端末）。name は空なら自動の名前 */
  const addPasskey = async (target?: PasskeyTarget, name?: string): Promise<boolean> => {
    setMsg(null); setBusy('add')
    try {
      await withReauth(async (extra) => {
        const options = await mfaFetch('/auth/mfa/passkeys/register/options', 'POST', extra)
        const response = await createPasskey(options, target)
        return mfaFetch('/auth/mfa/passkeys/register/verify', 'POST', { response, name: name ?? passkeyName, ...extra })
      })
      setPasskeyName('')
      await afterChange('パスキーを登録しました。')
      return true
    } catch (e) {
      if (!(e instanceof MfaApiError && e.code === 'CANCELLED')) {
        setMsg({ ok: false, text: e instanceof MfaApiError ? e.message : describePasskeyError(e) })
      }
      return false
    } finally { setBusy(null) }
  }

  /** アシスタント: メールが未確認なら、確認のダイアログを先に出して、確認後に run を実行する */
  const guard = (label: string, run: () => void) => {
    if (needsEmailGate({ isAssistant: !isAdminUser, emailVerified: s.email.verified, emailPlaceholder: s.email.placeholder })) {
      ev.reset(); setGate({ label, run }); return
    }
    run()
  }
  const cancelGate = () => { setGate(null); ev.reset() }

  const renamePasskey = async (id: string, current: string) => {
    const name = window.prompt('この端末の名前を入力してください（例: 仕事用PC / iPhone）', current)
    if (name === null || !name.trim()) return
    try {
      await mfaFetch(`/auth/mfa/passkeys/${encodeURIComponent(id)}`, 'PATCH', { name })
      await load()
    } catch (e) { setMsg({ ok: false, text: (e as Error).message }) }
  }

  const deletePasskey = async (id: string, name: string) => {
    if (!window.confirm(`「${name}」のパスキーを削除します。よろしいですか？\n（他の端末のセッションはログアウトされます）`)) return
    setMsg(null); setBusy(`del:${id}`)
    try {
      await withReauth((extra) => mfaFetch(`/auth/mfa/passkeys/${encodeURIComponent(id)}`, 'DELETE', extra))
      await afterChange('パスキーを削除しました。')
    } catch (e) {
      if (!(e instanceof MfaApiError && e.code === 'CANCELLED')) setMsg({ ok: false, text: (e as Error).message })
    } finally { setBusy(null) }
  }

  const changeMethod = async (method: MfaMethod): Promise<boolean> => {
    setMsg(null); setBusy(`m:${method}`)
    try {
      await withReauth((extra) => mfaFetch('/auth/mfa/method', 'PUT', { method, ...extra }))
      await afterChange(`認証方式を「${METHOD_LABEL[method]}」に設定しました。`)
      return true
    } catch (e) {
      if (!(e instanceof MfaApiError && e.code === 'CANCELLED')) setMsg({ ok: false, text: (e as Error).message })
      return false
    } finally { setBusy(null) }
  }

  // ── 表示 ─────────────────────────────────────────────────
  const methodBadge = s.method === 'none'
    ? <span className="badge badge-amber">未設定</span>
    : <span className="badge badge-green">{METHOD_LABEL[s.method]}</span>

  const optionBtn = (
    method: 'passkey' | 'email', title: string, desc: string, disabledReason: string | null
  ) => {
    const active = s.method === method
    return (
      <div key={method} style={{
        border: `1.5px solid ${active ? 'var(--green)' : 'var(--paper-3)'}`, borderRadius: 10, padding: '12px 14px',
        background: active ? 'var(--green-light)' : 'var(--paper)', opacity: disabledReason && !active ? 0.75 : 1,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{title}{active && <span className="badge badge-green" style={{ marginLeft: 8 }}>利用中</span>}</div>
          {!active && (
            <button className="btn btn-outline btn-sm" disabled={!!disabledReason || busy !== null}
              onClick={() => (method === 'email' && !isAdminUser
                ? guard('メール認証を設定する', () => void changeMethod(method))
                : void changeMethod(method))} style={{ minHeight: 34 }}>
              {busy === `m:${method}` ? '変更中...' : 'この方式にする'}
            </button>
          )}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.7, marginTop: 6 }}>{desc}</div>
        {disabledReason && !active && (
          <div style={{ fontSize: 12, color: 'var(--amber)', marginTop: 6 }}>※ {disabledReason}</div>
        )}
      </div>
    )
  }

  const setupBanner = (forceSetup || s.setup_required) && (
    <div style={{
      background: 'var(--amber-light)', border: '1px solid rgba(176,122,18,0.35)', borderRadius: 10,
      padding: '16px 18px', marginBottom: 20, display: 'flex', gap: 12, alignItems: 'flex-start',
    }}>
      <span style={{ fontSize: 22, lineHeight: 1.2, flexShrink: 0 }}>🛡️</span>
      <div>
        <div style={{ fontWeight: 700, color: 'var(--amber)', fontSize: 14, marginBottom: 6 }}>
          ログインの追加認証を設定してください
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.7 }}>
          {isAdminUser
            ? '管理者はパスキー認証が必須です。下の「パスキーを登録」から登録してください（システム保守担当者が発行した登録コードが必要です）。登録が完了するまで、他の管理機能は使えません。'
            : 'セキュリティ強化のため、追加認証が必要になりました。パスキー（おすすめ）か、メールの6桁コードのどちらかを設定してください。設定が完了するまで、他の機能は使えません。'}
        </div>
      </div>
    </div>
  )

  // 現在の状態（管理者向けの従来の表示）
  const statusCard = (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header"><span className="card-title">ログインの追加認証</span></div>
      <div style={sectionPad(isMobile)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>現在の方式</span>
          {methodBadge}
          {s.required && <span className="badge badge-blue">必須</span>}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.8, marginTop: 10 }}>
          {isAdminUser
            ? (passkeyPreparing
              ? 'パスキー機能は現在準備中です。準備ができるまでは、これまでどおりパスワードでログインできます。'
              : '管理者はパスキー認証のみ利用できます（メールコードでの代替はできません）。パスワードに加えて、端末の認証（Windows Hello・指紋/顔認証・PIN・セキュリティキー）でログインします。')
            : 'パスワードに加えて、ここで選んだ方式で本人確認をします。ログイン中は、操作のたびに再認証は求められません。'}
        </div>
        {isAdminUser && usablePasskeys === 1 && (
          <div style={{ fontSize: 12.5, color: 'var(--amber)', background: 'var(--amber-light)', borderRadius: 8, padding: '10px 12px', marginTop: 12, lineHeight: 1.7 }}>
            ⚠ パスキーが1つだけです。端末の故障・紛失に備えて、<strong>別の端末（スマートフォンやセキュリティキー）のパスキーも登録</strong>しておくことをおすすめします。
          </div>
        )}
        <MsgBanner msg={msg} />
      </div>
    </div>
  )

  // パスキー（まだ使えないときは「準備中」だけ。登録欄・専門用語は出さない）
  const passkeyCard = passkeyPreparing ? (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header"><span className="card-title">パスキー</span></div>
      <div style={{ ...sectionPad(isMobile), fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.8 }}>
        パスキー機能は現在準備中です。利用できるようになったら、ここから登録できます。
      </div>
    </div>
  ) : (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header"><span className="card-title">パスキー</span></div>
      <div style={sectionPad(isMobile)}>
        {!s.passkey_available && (
          <div style={{ fontSize: 13, color: 'var(--amber)', background: 'var(--amber-light)', borderRadius: 8, padding: '10px 12px', marginBottom: 14, lineHeight: 1.7 }}>
            パスキーは現在ご利用いただけません。登録済みのパスキーは削除されていません。利用できるようになるまで、しばらくお待ちください。
          </div>
        )}
        {s.passkey_available && !supported && (
          <div style={{ fontSize: 13, color: 'var(--amber)', background: 'var(--amber-light)', borderRadius: 8, padding: '10px 12px', marginBottom: 14, lineHeight: 1.7 }}>
            このブラウザ/端末ではパスキーを登録できません。最新の Chrome・Edge・Safari で、https のページから開いてください。
          </div>
        )}
        {s.passkey_available && supported && platformOk === false && (
          <div style={{ fontSize: 12.5, color: 'var(--ink-2)', background: 'var(--paper-2)', borderRadius: 8, padding: '10px 12px', marginBottom: 14, lineHeight: 1.7 }}>
            💡 この端末には画面ロック（Windows Hello の PIN・指紋/顔認証など）が設定されていない可能性があります。
            設定するか、スマートフォンやセキュリティキーで登録してください。
          </div>
        )}

        {!hasPasskey ? (
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 14 }}>登録済みのパスキーはありません。</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            {s.passkeys.map((p) => (
              <div key={p.id} style={{
                border: '1px solid var(--paper-3)', borderRadius: 10, padding: '12px 14px', background: 'var(--paper)',
                display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10,
                alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between',
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, wordBreak: 'break-all' }}>
                    🔑 {p.name}
                    {!p.usable && <span className="badge badge-red" style={{ marginLeft: 8 }}>現在は使えません</span>}
                    {p.backed_up && <span className="badge badge-gray" style={{ marginLeft: 8 }}>同期あり</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4, lineHeight: 1.7 }}>
                    登録: {fmtDate(p.created_at)} ／ 最終利用: {fmtDate(p.last_used_at)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button className="btn btn-outline btn-sm" onClick={() => void renamePasskey(p.id, p.name)} style={{ minHeight: 34 }}>名前を変更</button>
                  <button className="btn btn-outline btn-sm" disabled={busy !== null} onClick={() => void deletePasskey(p.id, p.name)}
                    style={{ minHeight: 34, color: 'var(--red)' }}>
                    {busy === `del:${p.id}` ? '削除中...' : '削除'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="form-group" style={{ marginBottom: 12 }}>
          <label>この端末の名前（任意）</label>
          <input aria-label="この端末の名前" value={passkeyName} onChange={(e) => setPasskeyName(e.target.value)} maxLength={40}
            placeholder={isAdminUser ? '例: 仕事用PC / iPhone / セキュリティキー' : '例: マイスマホ'} style={{ fontSize: 16, height: isMobile ? 46 : undefined, width: '100%', boxSizing: 'border-box' }} />
        </div>
        <button className="btn btn-primary" disabled={!s.passkey_available || !supported || busy !== null}
          onClick={() => (isAdminUser ? void addPasskey() : guard('パスキーを登録する', () => void addPasskey()))}
          style={{ width: isMobile ? '100%' : undefined, height: isMobile ? 48 : undefined }}>
          {busy === 'add' ? '登録中...' : hasPasskey ? '＋ 別のパスキーを追加' : '＋ パスキーを登録'}
        </button>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.7, marginTop: 10 }}>
          登録・削除のときは、本人確認（現在の認証方式）が必要です。最後の認証手段は削除できません。
          {isAdminUser && !hasPasskey && (
            <> 管理者の<strong>最初の1台</strong>は、パスワードだけでは登録できません。システム保守担当者が発行した使い捨ての登録コード（15分有効）を入力する画面が出ます。</>
          )}
        </div>
        {!isAdminUser && (
          <div style={{ fontSize: 12, color: 'var(--amber)', lineHeight: 1.7, marginTop: 8 }}>
            ※ スタジオの共用パソコン（Windows Hello など）には保存しないでください。パソコンの画面で追加するときは、「iPhone、iPad、または Android デバイス」を選んで、自分のスマホに登録します。
          </div>
        )}
      </div>
    </div>
  )

  // アシスタント: 認証方式の選択・変更
  const methodCard = (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header"><span className="card-title">認証方式の選択・変更</span></div>
      <div style={{ ...sectionPad(isMobile), display: 'flex', flexDirection: 'column', gap: 12 }}>
        {optionBtn(
          'passkey', '🔐 パスキー認証（おすすめ）',
          'スマートフォンの顔認証・指紋、Windows Hello、PIN、セキュリティキーでログインします。コードの入力は不要です。',
          !s.passkey_available ? 'パスキーは現在準備中です。'
            : usablePasskeys === 0 ? '先に上の「パスキー」から登録してください。' : null,
        )}
        {optionBtn(
          'email', '✉️ メール認証（6桁コード）',
          '登録・確認済みのメールアドレスに届く6桁のコード（有効5分・1回限り）を入力してログインします。',
          !s.email.mfa_available ? 'メール認証は現在ご利用いただけません。'
            : !emailReady ? 'メールアドレスが未確認です。押すと、先に確認の画面が開きます。' : null,
        )}
        {!s.required && s.method !== 'none' && (
          <button className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }} disabled={busy !== null}
            onClick={() => { if (window.confirm('追加認証をオフにします。パスワードだけでログインできるようになり、安全性が下がります。よろしいですか？')) void changeMethod('none') }}>
            追加認証をオフにする
          </button>
        )}
        <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.7 }}>
          方式の変更には、いま使っている方式での本人確認が必要です。ログイン画面から方式を切り替えることはできません。
        </div>
      </div>
    </div>
  )

  // アシスタント: メールアドレスの登録・確認（ステップ式・ダイアログと同じ状態・同じ画面を使う）
  const emailCard = (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header"><span className="card-title">メールアドレスの登録・確認</span></div>
      <div style={sectionPad(isMobile)}>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>現在のメールアドレス（ログインID）</div>
          <div style={{ fontSize: 14, fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>{s.email.address}</div>
          <div style={{ marginTop: 6 }}>
            {s.email.placeholder
              ? <span className="badge badge-amber">仮のアドレス（確認できません）</span>
              : s.email.verified
                ? <span className="badge badge-green">確認済み</span>
                : <span className="badge badge-amber">未確認</span>}
          </div>
        </div>
        <EmailVerifyPanel ev={ev} currentEmail={s.email.address} placeholder={s.email.placeholder} isMobile={isMobile}
          mfaAvailable={s.email.mfa_available} />
        {ev.changedTo && (
          <div role="status" style={{ background: 'var(--amber-light)', borderRadius: 8, padding: '10px 12px', fontSize: 13, lineHeight: 1.8, marginTop: 12 }}>
            <strong>ログインIDが変わりました。</strong>次回から <span style={{ fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>{ev.changedTo}</span> と今までのパスワードでログインします。
          </div>
        )}
      </div>
    </div>
  )

  const lostCard = isAdminUser && !passkeyPreparing && (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header"><span className="card-title">パスキーを紛失したとき</span></div>
      <div style={{ ...sectionPad(isMobile), fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.8 }}>
        <p style={{ margin: '0 0 8px' }}>
          メールの確認だけでパスキーを解除・再登録することはできません。別の端末に登録したパスキーがあれば、それでログインしてください。
        </p>
        <p style={{ margin: 0 }}>
          すべての端末を紛失した場合は、<strong>システム保守担当者（Cloudflare を管理する人）</strong>が発行する、
          使い捨て・15分有効の登録コードで復旧します。担当者向けの手順書は <code>docs/ADMIN_PASSKEY_RECOVERY.md</code> です。
        </p>
      </div>
    </div>
  )

  // アシスタント: はじめての設定はステップ式、設定済みは状態の表示＋「変更・管理」。管理者の画面は従来どおり
  if (!isAdminUser) {
    return (
      <>
        {dialog}
        {gate && (
          <EmailConfirmDialog ev={ev} currentEmail={s.email.address} placeholder={s.email.placeholder} isMobile={isMobile}
            mfaAvailable={s.email.mfa_available} reason={gate.label} onCancel={cancelGate} />
        )}
        {setupBanner}
        <AssistantSecurity ctl={{
          s, isMobile, forceSetup, busy, msg, clearMsg: () => setMsg(null),
          addPasskey, changeMethod, guard, ev,
          manage: <>{passkeyCard}{methodCard}{emailCard}</>,
        }} />
      </>
    )
  }

  return (
    <>
      {dialog}
      {setupBanner}
      {statusCard}
      {passkeyCard}
      {lostCard}
    </>
  )
}
